import React from "react";
import { DatePicker } from "@/components/ui/date-picker";
import { dateToLocalString, getToday, parseSelectableDate } from "../utils/dateUtils";

interface DateRangePickerProps {
  startDate: string;
  endDate: string;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  startDatePlaceholder?: string;
  endDatePlaceholder?: string;
  className?: string;
  hasError?: boolean;
  disabled?: boolean;
  // optional limits (YYYY-MM-DD or any parsable date string)
  minDate?: string;
  maxDate?: string;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  startDatePlaceholder = "From",
  endDatePlaceholder = "To",
  hasError = false,
  disabled = false,
  minDate,
  maxDate
}) => {
  const parsedMin = parseSelectableDate(minDate);
  const parsedMax = parseSelectableDate(maxDate);

  const getMinStartDate = () => {
    // se è passato un min esplicito, restituiscilo; altrimenti oggi
    return parsedMin ?? getToday();
  };

  const getMaxStartDate = () => {
    const end = endDate ? new Date(endDate) : undefined;
    if (end && parsedMax) {
      return new Date(Math.min(end.getTime(), parsedMax.getTime()));
    }
    return end ?? parsedMax;
  };

  const getMinEndDate = () => {
    const start = startDate ? new Date(startDate) : undefined;
    if (start && parsedMin) {
      return start > parsedMin ? start : parsedMin;
    }
    if (start) return start;
    return parsedMin ?? getToday();
  };

  const getMaxEndDate = () => {
    return parsedMax; // undefined if not provided
  };

  const handleStartDateChange = (date: string) => {
    const startDateObj = new Date(date);
    const today = getToday();

    // Only correct if start date is before today (invalid)
    if (startDateObj < today) {
      onStartDateChange(dateToLocalString(today));
      return;
    }

    // If start date is after end date, clear end date to force reselection
    if (endDate && startDateObj > new Date(endDate)) {
      onEndDateChange('');
    }

    onStartDateChange(date);
  };

  const handleEndDateChange = (date: string) => {
    const endDateObj = new Date(date);
    const today = getToday();
    const minEndDate = startDate ? new Date(startDate) : today;

    // Only correct if end date is before the minimum valid date (invalid)
    if (endDateObj < minEndDate) {
      onEndDateChange(dateToLocalString(minEndDate));
      return;
    }

    onEndDateChange(date);
  };

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Start Date */}
      <div className="space-y-2">
        <DatePicker
          value={startDate}
          onSelect={handleStartDateChange}
          placeholder={startDatePlaceholder}
          minDate={getMinStartDate()}
          maxDate={getMaxStartDate()}
          hasError={hasError}
          isDisabled={disabled}
        />
      </div>

      {/* End Date */}
      <div className="space-y-2">
        <DatePicker
          value={endDate}
          onSelect={handleEndDateChange}
          placeholder={endDatePlaceholder}
          minDate={getMinEndDate()}
          maxDate={getMaxEndDate()}
          hasError={hasError}
          isDisabled={disabled}
        />
      </div>
    </div>
  );
};
